import { lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

// Eagerly load only the Index page (landing page / LCP critical)
import Index from "./pages/Index";

// Lazy load all other pages
const Auth = lazy(() => import("./pages/Auth"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const Contrato = lazy(() => import("./pages/Contrato"));
const CriacaoContas = lazy(() => import("./pages/CriacaoContas"));
const Verificacao = lazy(() => import("./pages/Verificacao"));
const Profile = lazy(() => import("./pages/Profile"));
const Modelo = lazy(() => import("./pages/Modelo"));
const Calendario = lazy(() => import("./pages/Calendario"));
const InicioPostagens = lazy(() => import("./pages/InicioPostagens"));
const EnvioConteudo = lazy(() => import("./pages/EnvioConteudo"));
const ConteudoExclusivo = lazy(() => import("./pages/ConteudoExclusivo"));
const PrimeiraVenda = lazy(() => import("./pages/PrimeiraVenda"));
const Financeiro = lazy(() => import("./pages/Financeiro"));
const Afiliado = lazy(() => import("./pages/Afiliado"));
const Banco = lazy(() => import("./pages/Banco"));
const Contato = lazy(() => import("./pages/Contato"));
const Sobre = lazy(() => import("./pages/Sobre"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UniversalChat = lazy(() => import("./pages/UniversalChat"));
const ModelChat = lazy(() => import("./pages/ModelChat"));
const SolicitacaoCadastro = lazy(() => import("./pages/SolicitacaoCadastro"));
const DownloadBlankContract = lazy(() => import("./pages/DownloadBlankContract"));
const Notificacoes = lazy(() => import("./pages/Notificacoes"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminModelos = lazy(() => import("./pages/admin/AdminModelos"));
const AdminModeloDetalhe = lazy(() => import("./pages/admin/AdminModeloDetalhe"));
const AdminPipelines = lazy(() => import("./pages/admin/AdminPipelines"));
const AdminFinanceiro = lazy(() => import("./pages/admin/AdminFinanceiro"));
const AdminUsuarios = lazy(() => import("./pages/admin/AdminUsuarios"));
const AdminFuncionarios = lazy(() => import("./pages/admin/AdminFuncionarios"));
const AdminFuncionarioDetalhe = lazy(() => import("./pages/admin/AdminFuncionarioDetalhe"));
const AdminConfiguracoes = lazy(() => import("./pages/admin/AdminConfiguracoes"));
const AdminSolicitacoes = lazy(() => import("./pages/admin/AdminSolicitacoes"));
const AdminPagamentos = lazy(() => import("./pages/admin/AdminPagamentos"));

const AdminConteudoExclusivo = lazy(() => import("./pages/admin/AdminConteudoExclusivo"));
const AdminChatterBilling = lazy(() => import("./pages/admin/AdminChatterBilling"));
const AdminSnapsell = lazy(() => import("./pages/admin/AdminSnapsell"));
const AdminAtividadeRecente = lazy(() => import("./pages/admin/AdminAtividadeRecente"));
const AdminTodos = lazy(() => import("./pages/admin/AdminTodos"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMedia"));
const AdminSocialMediaFinanceiro = lazy(() => import("./pages/admin/AdminSocialMediaFinanceiro"));
const AdminConteudoDrive = lazy(() => import("./pages/admin/AdminConteudoDrive"));
const AdminPostagem = lazy(() => import("./pages/admin/AdminPostagem"));
const AdminVaProfiles = lazy(() => import("./pages/admin/AdminVaProfiles"));
const AdminVaProfileDetail = lazy(() => import("./pages/admin/AdminVaProfileDetail"));
const AdminCodigosFuncionarios = lazy(() => import("./pages/admin/AdminCodigosFuncionarios"));

import { AdminProtectedRoute } from "./components/admin/AdminProtectedRoute";

// Employee pages
const EmployeeDashboard = lazy(() => import("./pages/employee/EmployeeDashboard"));
const EmployeeModelos = lazy(() => import("./pages/employee/EmployeeModelos"));
const EmployeeModeloDetalhe = lazy(() => import("./pages/employee/EmployeeModeloDetalhe"));
const EmployeeCalendario = lazy(() => import("./pages/employee/EmployeeCalendario"));
const EmployeeConteudoExclusivo = lazy(() => import("./pages/employee/EmployeeConteudoExclusivo"));
const EmployeeChat = lazy(() => import("./pages/employee/EmployeeChat"));
const EmployeeFinanceiro = lazy(() => import("./pages/employee/EmployeeFinanceiro"));
const EmployeeFinanceiroChat = lazy(() => import("./pages/employee/EmployeeFinanceiroChat"));
const EmployeeConfiguracoes = lazy(() => import("./pages/employee/EmployeeConfiguracoes"));
const EmployeePostManager = lazy(() => import("./pages/employee/EmployeePostManager"));
const EmployeeFuncionarios = lazy(() => import("./pages/employee/EmployeeFuncionarios"));
const EmployeePagamentos = lazy(() => import("./pages/employee/EmployeePagamentos"));
const EmployeeUsuarios = lazy(() => import("./pages/employee/EmployeeUsuarios"));

const EmployeePagamentosSocios = lazy(() => import("./pages/employee/EmployeePagamentosSocios"));
const EmployeeSnapsell = lazy(() => import("./pages/employee/EmployeeSnapsell"));
const EmployeeAtividadeRecente = lazy(() => import("./pages/employee/EmployeeAtividadeRecente"));
const EmployeeTodosPage = lazy(() => import("./pages/employee/EmployeeTodos"));
const EmployeeSocialMedia = lazy(() => import("./pages/employee/EmployeeSocialMedia"));
const EmployeeSocialMediaFinanceiro = lazy(() => import("./pages/employee/EmployeeSocialMediaFinanceiro"));
const EmployeeConteudoDrive = lazy(() => import("./pages/employee/EmployeeConteudoDrive"));
const EmployeePostagem = lazy(() => import("./pages/employee/EmployeePostagem"));
const EmployeeCodigosCadastro = lazy(() => import("./pages/employee/EmployeeCodigosCadastro"));
const ContentManagerFuncionarios = lazy(() => import("./pages/employee/ContentManagerFuncionarios"));

import { EmployeeProtectedRoute } from "./components/employee/EmployeeProtectedRoute";

// VA Postagem pages
const VaPostagemDashboard = lazy(() => import("./pages/va-postagem/VaPostagemDashboard"));
const VaPostagemPostagem = lazy(() => import("./pages/va-postagem/VaPostagemPostagem"));
const VaPostagemConfiguracoes = lazy(() => import("./pages/va-postagem/VaPostagemConfiguracoes"));
const VaPostagemPostManager = lazy(() => import("./pages/va-postagem/VaPostagemPostManager"));
const VaPostagemCalendario = lazy(() => import("./pages/va-postagem/VaPostagemCalendario"));
const VaPostagemFinanceiro = lazy(() => import("./pages/va-postagem/VaPostagemFinanceiro"));
import { VaPostagemProtectedRoute } from "./components/va-postagem/VaPostagemProtectedRoute";

// AI Creator pages
const AiCreatorDashboard = lazy(() => import("./pages/ai-creator/AiCreatorDashboard"));
const AiCreatorContent = lazy(() => import("./pages/ai-creator/AiCreatorContent"));
const AiCreatorRules = lazy(() => import("./pages/ai-creator/AiCreatorRules"));
const AiCreatorFinancials = lazy(() => import("./pages/ai-creator/AiCreatorFinancials"));
const AiCreatorSettings = lazy(() => import("./pages/ai-creator/AiCreatorSettings"));
import { AiCreatorProtectedRoute } from "./components/ai-creator/AiCreatorProtectedRoute";

// Social Media VA pages
const SocialMediaVaDashboard = lazy(() => import("./pages/social-media-va/SocialMediaVaDashboard"));
const SocialMediaVaPlatform = lazy(() => import("./pages/social-media-va/SocialMediaVaPlatform"));
const SocialMediaVaSettings = lazy(() => import("./pages/social-media-va/SocialMediaVaSettings"));
const SocialMediaVaFinanceiro = lazy(() => import("./pages/social-media-va/SocialMediaVaFinanceiro"));
import { SocialMediaVaProtectedRoute } from "./components/social-media-va/SocialMediaVaProtectedRoute";

// Recruiter pages
const RecruiterDashboard = lazy(() => import("./pages/recruiter/RecruiterDashboard"));
const RecruiterModelos = lazy(() => import("./pages/recruiter/RecruiterModelos"));
const RecruiterLoja = lazy(() => import("./pages/recruiter/RecruiterLoja"));
const RecruiterProfile = lazy(() => import("./pages/recruiter/RecruiterProfile"));
import { RecruiterProtectedRoute } from "./components/recruiter/RecruiterProtectedRoute";

// Recruiter Admin pages
const RecruiterAdminCalendar = lazy(() => import("./pages/recruiter-admin/RecruiterAdminCalendar"));
const RecruiterAdminAffiliateModels = lazy(() => import("./pages/recruiter-admin/RecruiterAdminAffiliateModels"));
const RecruiterAdminRecruiters = lazy(() => import("./pages/recruiter-admin/RecruiterAdminRecruiters"));
const RecruiterAdminCommissions = lazy(() => import("./pages/recruiter-admin/RecruiterAdminCommissions"));
const RecruiterAdminLoja = lazy(() => import("./pages/recruiter-admin/RecruiterAdminLoja"));
const RecruiterAdminProfile = lazy(() => import("./pages/recruiter-admin/RecruiterAdminProfile"));
const RecruiterAdminMetas = lazy(() => import("./pages/recruiter-admin/RecruiterAdminMetas"));
import { RecruiterAdminProtectedRoute } from "./components/recruiter-admin/RecruiterAdminProtectedRoute";

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <LanguageProvider>
            <PWAUpdatePrompt />
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
              {/* Public routes */}
              <Route path="/solicitacao" element={<SolicitacaoCadastro />} />
              <Route path="/download-contrato" element={<DownloadBlankContract />} />

              {/* Model/User routes */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/contrato" element={<Contrato />} />
              <Route path="/criacao-contas" element={<CriacaoContas />} />
              <Route path="/verificacao" element={<Verificacao />} />
              <Route path="/envio-conteudo" element={<EnvioConteudo />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/modelo" element={<Modelo />} />
              <Route path="/calendario" element={<Calendario />} />
              <Route path="/inicio-postagens" element={<InicioPostagens />} />
              <Route path="/conteudo-exclusivo" element={<ConteudoExclusivo />} />
              <Route path="/primeira-venda" element={<PrimeiraVenda />} />
              <Route path="/financeiro" element={<Financeiro />} />
              <Route path="/afiliado" element={<Afiliado />} />
              <Route path="/banco" element={<Banco />} />
              <Route path="/mensagens" element={<ModelChat />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/notificacoes" element={<Notificacoes />} />

              {/* Admin routes */}
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/modelos" element={<AdminProtectedRoute><AdminModelos /></AdminProtectedRoute>} />
              <Route path="/admin/modelos/:id" element={<AdminProtectedRoute><AdminModeloDetalhe /></AdminProtectedRoute>} />
              <Route path="/admin/funcionarios" element={<AdminProtectedRoute><AdminFuncionarios /></AdminProtectedRoute>} />
              <Route path="/admin/funcionarios/:id" element={<AdminProtectedRoute><AdminFuncionarioDetalhe /></AdminProtectedRoute>} />
              <Route path="/admin/pipelines" element={<AdminProtectedRoute><AdminPipelines /></AdminProtectedRoute>} />
              <Route path="/admin/solicitacoes" element={<AdminProtectedRoute><AdminSolicitacoes /></AdminProtectedRoute>} />
              <Route path="/admin/financeiro" element={<AdminProtectedRoute><AdminFinanceiro /></AdminProtectedRoute>} />
              <Route path="/admin/pagamentos" element={<AdminProtectedRoute><AdminPagamentos /></AdminProtectedRoute>} />
              <Route path="/admin/usuarios" element={<AdminProtectedRoute><AdminUsuarios /></AdminProtectedRoute>} />
              <Route path="/admin/configuracoes" element={<AdminProtectedRoute><AdminConfiguracoes /></AdminProtectedRoute>} />
              
              <Route path="/admin/conteudo-exclusivo" element={<AdminProtectedRoute><AdminConteudoExclusivo /></AdminProtectedRoute>} />
              <Route path="/admin/chat" element={<AdminProtectedRoute><UniversalChat /></AdminProtectedRoute>} />
              <Route path="/admin/chatter-billing" element={<AdminProtectedRoute><AdminChatterBilling /></AdminProtectedRoute>} />
              <Route path="/admin/snapsell" element={<AdminProtectedRoute><AdminSnapsell /></AdminProtectedRoute>} />
              <Route path="/admin/atividade-recente" element={<AdminProtectedRoute><AdminAtividadeRecente /></AdminProtectedRoute>} />
              <Route path="/admin/todos" element={<AdminProtectedRoute><AdminTodos /></AdminProtectedRoute>} />
              <Route path="/admin/social-media" element={<AdminProtectedRoute><AdminSocialMedia /></AdminProtectedRoute>} />
              <Route path="/admin/social-media-financeiro" element={<AdminProtectedRoute><AdminSocialMediaFinanceiro /></AdminProtectedRoute>} />
              <Route path="/admin/conteudo-drive" element={<AdminProtectedRoute><AdminConteudoDrive /></AdminProtectedRoute>} />
              <Route path="/admin/postagem" element={<AdminProtectedRoute><AdminPostagem /></AdminProtectedRoute>} />
              <Route path="/admin/va-profiles" element={<AdminProtectedRoute><AdminVaProfiles /></AdminProtectedRoute>} />
              <Route path="/admin/va-profiles/:id" element={<AdminProtectedRoute><AdminVaProfileDetail /></AdminProtectedRoute>} />
              <Route path="/admin/codigos-funcionarios" element={<AdminProtectedRoute><AdminCodigosFuncionarios /></AdminProtectedRoute>} />

              {/* Employee routes */}
              <Route path="/employee" element={<EmployeeProtectedRoute><EmployeeDashboard /></EmployeeProtectedRoute>} />
              <Route path="/employee/modelos" element={<EmployeeProtectedRoute><EmployeeModelos /></EmployeeProtectedRoute>} />
              <Route path="/employee/modelos/:id" element={<EmployeeProtectedRoute><EmployeeModeloDetalhe /></EmployeeProtectedRoute>} />
              <Route path="/employee/calendario" element={<EmployeeProtectedRoute><EmployeeCalendario /></EmployeeProtectedRoute>} />
              <Route path="/employee/post-manager" element={<EmployeeProtectedRoute><EmployeePostManager /></EmployeeProtectedRoute>} />
              <Route path="/employee/cm-funcionarios" element={<EmployeeProtectedRoute><ContentManagerFuncionarios /></EmployeeProtectedRoute>} />
              <Route path="/employee/conteudo-exclusivo" element={<EmployeeProtectedRoute><EmployeeConteudoExclusivo /></EmployeeProtectedRoute>} />
              <Route path="/employee/chat" element={<EmployeeProtectedRoute><EmployeeChat /></EmployeeProtectedRoute>} />
              <Route path="/employee/financeiro" element={<EmployeeProtectedRoute><EmployeeFinanceiro /></EmployeeProtectedRoute>} />
              <Route path="/employee/financeiro-chat" element={<EmployeeProtectedRoute><EmployeeFinanceiroChat /></EmployeeProtectedRoute>} />
              <Route path="/employee/configuracoes" element={<EmployeeProtectedRoute><EmployeeConfiguracoes /></EmployeeProtectedRoute>} />
              <Route path="/employee/mensagens" element={<EmployeeProtectedRoute><UniversalChat /></EmployeeProtectedRoute>} />
              <Route path="/employee/funcionarios" element={<EmployeeProtectedRoute><EmployeeFuncionarios /></EmployeeProtectedRoute>} />
              <Route path="/employee/usuarios" element={<EmployeeProtectedRoute><EmployeeUsuarios /></EmployeeProtectedRoute>} />
              {/* Chatter/Supervisor Chatter routes removed */}
              <Route path="/employee/pagamentos" element={<EmployeeProtectedRoute><EmployeePagamentos /></EmployeeProtectedRoute>} />
              <Route path="/employee/todos" element={<EmployeeProtectedRoute><EmployeeTodosPage /></EmployeeProtectedRoute>} />
              <Route path="/employee/codigos-cadastro" element={<EmployeeProtectedRoute><EmployeeCodigosCadastro /></EmployeeProtectedRoute>} />


              <Route path="/employee/pagamentos-socios" element={<EmployeeProtectedRoute><EmployeePagamentosSocios /></EmployeeProtectedRoute>} />
              <Route path="/employee/snapsell" element={<EmployeeProtectedRoute><EmployeeSnapsell /></EmployeeProtectedRoute>} />
              <Route path="/employee/atividade-recente" element={<EmployeeProtectedRoute><EmployeeAtividadeRecente /></EmployeeProtectedRoute>} />
              <Route path="/employee/social-media" element={<EmployeeProtectedRoute><EmployeeSocialMedia /></EmployeeProtectedRoute>} />
              <Route path="/employee/social-media-financeiro" element={<EmployeeProtectedRoute><EmployeeSocialMediaFinanceiro /></EmployeeProtectedRoute>} />
              <Route path="/employee/conteudo-drive" element={<EmployeeProtectedRoute><EmployeeConteudoDrive /></EmployeeProtectedRoute>} />
              <Route path="/employee/postagem" element={<EmployeeProtectedRoute><EmployeePostagem /></EmployeeProtectedRoute>} />

              {/* Recruiter routes */}
              <Route path="/recruiter" element={<RecruiterProtectedRoute><RecruiterDashboard /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/modelos" element={<RecruiterProtectedRoute><RecruiterModelos /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/loja" element={<RecruiterProtectedRoute><RecruiterLoja /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/perfil" element={<RecruiterProtectedRoute><RecruiterProfile /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/metas" element={<RecruiterProtectedRoute><RecruiterAdminMetas /></RecruiterProtectedRoute>} />

              {/* Recruiter Admin routes */}
              <Route path="/recruiter-admin" element={<RecruiterAdminProtectedRoute><RecruiterAdminCommissions /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/calendario" element={<RecruiterAdminProtectedRoute><RecruiterAdminCalendar /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/modelos-afiliados" element={<RecruiterAdminProtectedRoute><RecruiterAdminAffiliateModels /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/recrutadores" element={<RecruiterAdminProtectedRoute><RecruiterAdminRecruiters /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/loja" element={<RecruiterAdminProtectedRoute><RecruiterAdminLoja /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/perfil" element={<RecruiterAdminProtectedRoute><RecruiterAdminProfile /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/metas" element={<RecruiterAdminProtectedRoute><RecruiterAdminMetas /></RecruiterAdminProtectedRoute>} />

              {/* VA Postagem routes */}
              <Route path="/va-postagem" element={<VaPostagemProtectedRoute><VaPostagemDashboard /></VaPostagemProtectedRoute>} />
              <Route path="/va-postagem/postagem" element={<VaPostagemProtectedRoute><VaPostagemPostagem /></VaPostagemProtectedRoute>} />
              <Route path="/va-postagem/post-manager" element={<VaPostagemProtectedRoute><VaPostagemPostManager /></VaPostagemProtectedRoute>} />
              <Route path="/va-postagem/calendario" element={<VaPostagemProtectedRoute><VaPostagemCalendario /></VaPostagemProtectedRoute>} />
              <Route path="/va-postagem/financeiro" element={<VaPostagemProtectedRoute><VaPostagemFinanceiro /></VaPostagemProtectedRoute>} />
              <Route path="/va-postagem/configuracoes" element={<VaPostagemProtectedRoute><VaPostagemConfiguracoes /></VaPostagemProtectedRoute>} />

              {/* AI Creator routes */}
              <Route path="/ai-creator" element={<AiCreatorProtectedRoute><AiCreatorDashboard /></AiCreatorProtectedRoute>} />
              <Route path="/ai-creator/content" element={<AiCreatorProtectedRoute><AiCreatorContent /></AiCreatorProtectedRoute>} />
              <Route path="/ai-creator/rules" element={<AiCreatorProtectedRoute><AiCreatorRules /></AiCreatorProtectedRoute>} />
              <Route path="/ai-creator/financials" element={<AiCreatorProtectedRoute><AiCreatorFinancials /></AiCreatorProtectedRoute>} />
              <Route path="/ai-creator/settings" element={<AiCreatorProtectedRoute><AiCreatorSettings /></AiCreatorProtectedRoute>} />

              {/* Social Media VA routes */}
              <Route path="/social-media-va" element={<SocialMediaVaProtectedRoute><SocialMediaVaDashboard /></SocialMediaVaProtectedRoute>} />
              <Route path="/social-media-va/platform" element={<SocialMediaVaProtectedRoute><SocialMediaVaPlatform /></SocialMediaVaProtectedRoute>} />
              <Route path="/social-media-va/settings" element={<SocialMediaVaProtectedRoute><SocialMediaVaSettings /></SocialMediaVaProtectedRoute>} />
              <Route path="/social-media-va/financeiro" element={<SocialMediaVaProtectedRoute><SocialMediaVaFinanceiro /></SocialMediaVaProtectedRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;